import { useCallback } from 'react';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

// Module-level state for script loading
let grecaptchaLoading = null;
let grecaptchaLoaded = false;

function loadRecaptchaScript(siteKey) {
  return new Promise(resolve => {
    if (typeof window === 'undefined' || !siteKey) {
      resolve(null);
      return;
    }
    if (window.grecaptcha && window.grecaptcha.execute) {
      resolve(window.grecaptcha);
      return;
    }
    if (grecaptchaLoading) {
      grecaptchaLoading.then(resolve);
      return;
    }
    grecaptchaLoading = new Promise(resolveLoad => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => {
        grecaptchaLoaded = true;
        resolveLoad(window.grecaptcha || null);
      };
      script.onerror = () => {
        grecaptchaLoaded = false;
        resolveLoad(null);
      };
      document.head.appendChild(script);
    });
    grecaptchaLoading.then(resolve);
  });
}

/**
 * Hook to get reCAPTCHA token for form submissions.
 * Returns a function that returns a Promise<string | ''>.
 * If reCAPTCHA is not configured, returns empty string.
 *
 * @param {string} action - Action name (e.g. 'login', 'register', 'contact', 'create_agent')
 * @returns {() => Promise<string>} Function to execute and get token
 */
export function useRecaptcha(action = 'submit') {
  const execute = useCallback(async () => {
    if (!RECAPTCHA_SITE_KEY) return '';
    try {
      const grecaptcha = await loadRecaptchaScript(RECAPTCHA_SITE_KEY);
      if (grecaptcha && grecaptcha.execute) {
        const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
        return token || '';
      }
    } catch (err) {
      console.warn('reCAPTCHA execution failed', err);
    }
    return '';
  }, [action]);

  return execute;
}
